import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { useCallback } from "react";

// GraphQL Queries and Mutations
const DATABASE_RELATIONSHIPS_QUERIES_AND_MUTATIONS = {
  GET_RELATIONSHIPS: gql`
    query GetRelationships($userDbId: Int!) {
      GetRelationships(user_db_id: $userDbId) {
        id
        user_id
        user_db_id
        source_table
        destination_table
        source_column
        destination_column
        is_active
        created_at
        updated_at
      }
    }
  `,

  ADD_RELATIONSHIP: gql`
    mutation AddRelationship($userDbId: Int!, $sourceTable: String!, $destinationTable: String!, $sourceColumn: String!, $destinationColumn: String!) {
      AddRelationship(user_db_id: $userDbId, source_table: $sourceTable, destination_table: $destinationTable, source_column: $sourceColumn, destination_column: $destinationColumn) {
        id
        user_id
        user_db_id
        source_table
        destination_table
        source_column
        destination_column
        is_active
        created_at
        updated_at
      }
    }
  `,

  DELETE_RELATIONSHIPS: gql`
    mutation DeleteRelationshipsByUserAndDb($userDbId: Int!, $id: Int!) {
      DeleteRelationshipsByUserAndDb(user_db_id: $userDbId, id: $id) {
        success
        message
        deletedCount
      }
    }
  `,
};

export const useDatabaseRelationships = () => {
  const [addRelationshipMutation, { 
    loading: addLoading, 
    error: addError, 
    data: addData 
  }] = useMutation(
    DATABASE_RELATIONSHIPS_QUERIES_AND_MUTATIONS.ADD_RELATIONSHIP
  );

  const [getRelationshipsQuery, { 
    loading: getRelationshipsLoading, 
    error: getRelationshipsError, 
    data: getRelationshipsData 
  }] = useLazyQuery(
    DATABASE_RELATIONSHIPS_QUERIES_AND_MUTATIONS.GET_RELATIONSHIPS,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [deleteRelationshipsMutation, { 
    loading: deleteLoading, 
    error: deleteError, 
    data: deleteData 
  }] = useMutation(
    DATABASE_RELATIONSHIPS_QUERIES_AND_MUTATIONS.DELETE_RELATIONSHIPS
  );

  const addRelationship = useCallback(async ({
    userDbId,
    sourceTable,
    destinationTable,
    sourceColumn,
    destinationColumn,
  }) => {
    try {
      const { data } = await addRelationshipMutation({
        variables: {
          userDbId: parseInt(userDbId),
          sourceTable,
          destinationTable,
          sourceColumn,
          destinationColumn,
        },
      });

      if (!data?.AddRelationship) {
        throw new Error("Failed to add relationship");
      }

      return {
        success: true,
        data: data.AddRelationship,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while adding relationship",
      };
    }
  }, [addRelationshipMutation]);

  const getRelationships = useCallback(async (userDbId) => {
    try {
      const { data } = await getRelationshipsQuery({
        variables: {
          userDbId: parseInt(userDbId),
        },
      });

      if (!data?.GetRelationships) {
        throw new Error("Failed to fetch relationships");
      }

      return {
        success: true,
        data: data.GetRelationships,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while fetching relationships",
      };
    }
  }, [getRelationshipsQuery]);

  const deleteRelationships = useCallback(async (userDbId, id) => {
    try {
      const { data } = await deleteRelationshipsMutation({
        variables: {
          userDbId: parseInt(userDbId),
          id: parseInt(id),
        },
      });

      if (!data?.DeleteRelationshipsByUserAndDb) {
        throw new Error("Failed to delete relationships");
      }

      return {
        success: true,
        data: data.DeleteRelationshipsByUserAndDb,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred while deleting relationships",
      };
    }
  }, [deleteRelationshipsMutation]);

  return {
    addRelationship,
    getRelationships,
    deleteRelationships,
    loading: addLoading || getRelationshipsLoading || deleteLoading,
    error: addError || getRelationshipsError || deleteError,
    data: addData || getRelationshipsData || deleteData,
  };
}; 